import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Clock, MapPin, Loader2, CreditCard, Banknote, X, ReceiptText, List, CalendarIcon, ChevronRight, Zap, Users, UserPlus, Trash2 } from "lucide-react";
import { BookingsGroupedView } from "@/components/BookingsGroupedView";
import ShareDialog from "@/components/ShareDialog";
import { format, startOfMonth, endOfMonth, isSameDay, parseISO } from "date-fns";

const statusStyle = {
  CONFIRMED: "status-confirmed", COMPLETED: "status-confirmed",
  PENDING_PAYMENT: "status-pending", PENDING_CASH: "status-pending",
  CANCELLED: "status-cancelled", EXPIRED: "status-cancelled",
};
const statusLabel = {
  CONFIRMED: "Confirmed", COMPLETED: "Completed",
  PENDING_PAYMENT: "Awaiting Payment", PENDING_CASH: "Pay at Venue",
  CANCELLED: "Cancelled", EXPIRED: "Expired",
};

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list" | "calendar"
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarItems, setCalendarItems] = useState([]);
  const [shareDialog, setShareDialog] = useState(null); // { url, title, desc }
  const [sharingId, setSharingId] = useState(null);
  const [groupData, setGroupData] = useState({}); // bookingId -> group info
  const [groupDialog, setGroupDialog] = useState(null); // booking_id to show group
  const [removingMember, setRemovingMember] = useState(null);
  const navigate = useNavigate();

  const fetchBookings = async () => {
    try {
      const res = await api.get("/bookings/my");
      setBookings(res.data);
      // Fetch group data for confirmed bookings
      const confirmed = (res.data || []).filter(b => ["CONFIRMED", "COMPLETED", "PENDING_CASH"].includes(b.status));
      const gd = {};
      await Promise.all(confirmed.map(async b => {
        try {
          const gr = await api.get(`/bookings/${b.booking_id}/group`);
          if (gr.data?.is_shared) gd[b.booking_id] = gr.data;
        } catch {}
      }));
      setGroupData(gd);
    } catch { setBookings([]); }
    setLoading(false);
  };

  useEffect(() => { fetchBookings(); }, []);

  const handleShare = async (bookingId) => {
    setSharingId(bookingId);
    try {
      const res = await api.post(`/bookings/${bookingId}/share`);
      const token = res.data.share_token;
      const joinUrl = `${window.location.origin}/join/${token}`;
      const b = bookings.find(x => x.booking_id === bookingId);
      setShareDialog({
        url: joinUrl,
        title: `Join my ${b?.sport_type || "game"} at ${b?.venue_name || "SetPlay"}`,
        desc: `${b?.start_dt?.slice(0, 10)} ${b?.start_dt?.slice(11, 16)} - ${b?.end_dt?.slice(11, 16)} | ${b?.resource_name}`,
      });
      // Also update group data
      setGroupData(prev => ({
        ...prev,
        [bookingId]: { ...prev[bookingId], share_token: token, is_shared: true, max_players: res.data.max_players, members: res.data.members || [] },
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create share link");
    }
    setSharingId(null);
  };

  const handleRemoveMember = async (bookingId, memberId) => {
    setRemovingMember(memberId);
    try {
      await api.delete(`/bookings/${bookingId}/group/${memberId}`);
      toast.success("Member removed");
      // Refresh group data
      const gr = await api.get(`/bookings/${bookingId}/group`);
      setGroupData(prev => ({ ...prev, [bookingId]: gr.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove member");
    }
    setRemovingMember(null);
  };

  // Fetch calendar data when in calendar mode
  useEffect(() => {
    if (viewMode !== "calendar") return;
    const start = format(startOfMonth(calendarDate), "yyyy-MM-dd");
    const end = format(endOfMonth(calendarDate), "yyyy-MM-dd");
    api.get("/bookings/calendar", { params: { start, end } })
      .then(r => setCalendarItems(r.data || []))
      .catch(() => setCalendarItems([]));
  }, [viewMode, calendarDate]);

  const handleCancel = async () => {
    if (!cancelDialog) return;
    setCancelling(true);
    try {
      await api.post(`/bookings/${cancelDialog}/cancel`);
      toast.success("Booking cancelled");
      fetchBookings();
    } catch (err) { toast.error(err.response?.data?.detail || "Cancel failed"); }
    setCancelling(false);
    setCancelDialog(null);
  };

  // Recent booking = most recent non-cancelled booking
  const recentBooking = bookings.find(b => !["CANCELLED", "EXPIRED"].includes(b.status));

  // Calendar day styling — highlight days with bookings
  const bookedDates = calendarItems.filter(c => c.type === "booking").map(c => c.date);
  const calendarModifiers = {
    booked: bookedDates.map(d => parseISO(d)),
  };
  const calendarModifiersStyles = {
    booked: { backgroundColor: "rgb(249 115 22 / 0.15)", borderRadius: "0", fontWeight: "bold" },
  };

  // Items for selected calendar day
  const selectedDayItems = calendarItems.filter(c => c.date === format(calendarDate, "yyyy-MM-dd"));

  const PAYMENT_LABELS = { ONLINE: "Stripe Checkout", EPLAYMENT: "ePlayment", CASH: "Pay at Venue", WALLET: "Wallet", VENUE_DEPOSIT: "Venue Deposit" };

  const BookingCard = (b) => {
    const group = groupData[b.booking_id];
    const memberCount = group ? group.members?.length || 0 : 0;
    const canShare = ["CONFIRMED", "PENDING_CASH"].includes(b.status);
    const isPaid = ["CONFIRMED", "COMPLETED"].includes(b.status) && Number(b.total_amount) > 0;
    return (
      <div className="bg-white border-2 border-slate-100 p-4 flex flex-col gap-3 hover:border-slate-200 transition-colors" data-testid={`booking-${b.booking_id}`}>
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading text-base font-bold uppercase tracking-tight">{b.venue_name}</h3>
              <Badge className={`${statusStyle[b.status] || ''} text-[10px] font-heading uppercase`}>{statusLabel[b.status] || b.status}</Badge>
              {group?.is_shared && (
                <Badge className="bg-orange-100 text-orange-700 text-[10px] font-heading uppercase cursor-pointer hover:bg-orange-200"
                  onClick={() => setGroupDialog(b.booking_id)} data-testid={`group-badge-${b.booking_id}`}>
                  <Users className="w-3 h-3 mr-1" />{memberCount + 1}/{group.max_players}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{b.resource_name} <span className="capitalize">({b.sport_type})</span></p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{b.start_dt?.slice(0, 10)}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{b.start_dt?.slice(11, 16)} - {b.end_dt?.slice(11, 16)}</span>
            </div>
            {/* Payment Info */}
            {isPaid && (
              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1" data-testid={`payment-info-${b.booking_id}`}>
                <span className="flex items-center gap-1">
                  {b.payment_mode === "ONLINE" ? <CreditCard className="w-3 h-3 text-blue-500" /> : <Banknote className="w-3 h-3 text-green-600" />}
                  <span className="font-medium text-foreground">{b.payment_method_display || PAYMENT_LABELS[b.payment_mode] || b.payment_mode}</span>
                </span>
                {b.gateway_txn_id && (
                  <span className="font-mono text-[10px] bg-slate-100 px-2 py-0.5 rounded" data-testid={`txn-id-${b.booking_id}`}>
                    TXN: {b.gateway_txn_id.length > 24 ? `${b.gateway_txn_id.slice(0, 24)}...` : b.gateway_txn_id}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-right">
              <p className="font-heading text-lg font-bold text-court-orange">{"\u20B1"}{b.total_amount?.toFixed(0)}</p>
            </div>
            {canShare && (
              <Button data-testid={`share-btn-${b.booking_id}`} size="sm" variant="outline"
                onClick={() => handleShare(b.booking_id)} disabled={sharingId === b.booking_id}
                className="rounded-full text-xs border-2 border-orange-300 text-orange-600 hover:bg-orange-50">
                {sharingId === b.booking_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />}
                Invite
              </Button>
            )}
            {b.status === "PENDING_PAYMENT" && (
              <Button data-testid={`pay-btn-${b.booking_id}`} size="sm" onClick={() => navigate(`/checkout/${b.booking_id}`)}
                className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase text-xs px-4">Pay Now</Button>
            )}
            {isPaid && (
              <Button data-testid={`receipt-btn-${b.booking_id}`} size="sm" variant="outline" onClick={() => navigate(`/receipts/booking/${b.booking_id}`)}
                className="rounded-full text-xs border-2">
                <ReceiptText className="w-3 h-3 mr-1" />Receipt
              </Button>
            )}
            {!["CANCELLED","EXPIRED","COMPLETED"].includes(b.status) && (
              <Button data-testid={`cancel-btn-${b.booking_id}`} size="sm" variant="outline" onClick={() => setCancelDialog(b.booking_id)}
                className="rounded-full text-xs border-2"><X className="w-3 h-3" /></Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight" data-testid="my-bookings-title">My Bookings</h1>
        {/* View Toggle */}
        <div className="flex border-2 border-slate-200 rounded-sm overflow-hidden" data-testid="view-toggle">
          <button onClick={() => setViewMode("list")} data-testid="view-list-btn"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading uppercase ${viewMode === "list" ? "bg-primary text-white" : "bg-white text-muted-foreground hover:bg-muted/50"}`}>
            <List className="w-3.5 h-3.5" />List
          </button>
          <button onClick={() => setViewMode("calendar")} data-testid="view-calendar-btn"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading uppercase ${viewMode === "calendar" ? "bg-primary text-white" : "bg-white text-muted-foreground hover:bg-muted/50"}`}>
            <CalendarIcon className="w-3.5 h-3.5" />Calendar
          </button>
        </div>
      </div>

      {/* Recent Booking Snapshot */}
      {recentBooking && (
        <div className="mb-6 bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-sm p-4" data-testid="recent-booking-tile">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-orange-500" />
            <span className="text-xs font-heading uppercase tracking-[0.15em] text-orange-600">Recent Booking</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight" data-testid="recent-venue">
                {recentBooking.venue_name}
                {groupData[recentBooking.booking_id]?.is_shared && (
                  <Badge className="ml-2 bg-orange-100 text-orange-700 text-[10px] font-heading uppercase cursor-pointer hover:bg-orange-200 align-middle"
                    onClick={() => setGroupDialog(recentBooking.booking_id)} data-testid="recent-group-badge">
                    <Users className="w-3 h-3 mr-1" />
                    {(groupData[recentBooking.booking_id]?.members?.length || 0) + 1}/{groupData[recentBooking.booking_id]?.max_players}
                  </Badge>
                )}
              </h3>
              <p className="text-sm text-muted-foreground">{recentBooking.resource_name} <span className="capitalize">({recentBooking.sport_type})</span></p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{recentBooking.start_dt?.slice(0, 10)}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{recentBooking.start_dt?.slice(11, 16)} - {recentBooking.end_dt?.slice(11, 16)}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="font-heading text-xl font-bold text-orange-600" data-testid="recent-amount">{"\u20B1"}{recentBooking.total_amount?.toFixed(0)}</p>
                <Badge className={`${statusStyle[recentBooking.status] || ''} text-[10px] font-heading uppercase`}>{statusLabel[recentBooking.status] || recentBooking.status}</Badge>
              </div>
              {recentBooking.status === "PENDING_PAYMENT" && (
                <Button size="sm" onClick={() => navigate(`/checkout/${recentBooking.booking_id}`)}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full font-heading uppercase text-xs px-4">Pay Now</Button>
              )}
              {["CONFIRMED", "COMPLETED"].includes(recentBooking.status) && Number(recentBooking.total_amount) > 0 && (
                <Button size="sm" variant="outline" onClick={() => navigate(`/receipts/booking/${recentBooking.booking_id}`)}
                  className="rounded-full text-xs border-2 border-orange-300 text-orange-600 hover:bg-orange-50">
                  <ReceiptText className="w-3 h-3 mr-1" />Receipt
                </Button>
              )}
              {["CONFIRMED", "PENDING_CASH"].includes(recentBooking.status) && (
                <Button size="sm" variant="outline" onClick={() => handleShare(recentBooking.booking_id)}
                  disabled={sharingId === recentBooking.booking_id}
                  className="rounded-full text-xs border-2 border-orange-300 text-orange-600 hover:bg-orange-50"
                  data-testid="recent-invite-btn">
                  {sharingId === recentBooking.booking_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />}
                  Invite
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-testid="calendar-view">
          <div>
            <Calendar
              mode="single"
              selected={calendarDate}
              onSelect={d => { if (d) setCalendarDate(d); }}
              onMonthChange={setCalendarDate}
              className="border-2 border-slate-200 p-3"
              classNames={{ day_today: "bg-orange-100 text-orange-700 font-bold" }}
              modifiers={calendarModifiers}
              modifiersStyles={calendarModifiersStyles}
              data-testid="bookings-calendar"
            />
          </div>
          <div>
            <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-3">
              {format(calendarDate, "MMMM d, yyyy")}
            </h3>
            {selectedDayItems.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No bookings on this day</p>
            ) : (
              <div className="space-y-2">
                {selectedDayItems.map(item => (
                  <div key={item.id} className="border-2 border-slate-100 p-3 rounded-sm hover:border-slate-200 transition-colors" data-testid={`cal-item-${item.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-heading text-sm font-bold uppercase">{item.title}</p>
                        <p className="text-xs text-muted-foreground">{item.start} - {item.end} {item.sport && <span className="capitalize">({item.sport})</span>}</p>
                      </div>
                      <div className="text-right">
                        {item.amount > 0 && <p className="font-heading font-bold text-court-orange">{"\u20B1"}{item.amount?.toFixed(0)}</p>}
                        <Badge variant="outline" className="text-[9px]" style={{ borderColor: item.color, color: item.color }}>{item.status}</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <BookingsGroupedView
          bookings={bookings}
          renderItem={BookingCard}
          waitlistCount={0}
          waitlistItems={[]}
        />
      )}

      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Cancel Booking?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setCancelDialog(null)} className="flex-1 rounded-full border-2">Keep Booking</Button>
            <Button data-testid="confirm-cancel-btn" onClick={handleCancel} disabled={cancelling}
              className="flex-1 bg-destructive hover:bg-destructive/90 text-white rounded-full font-heading uppercase">
              {cancelling ? "Cancelling..." : "Cancel It"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Members Dialog */}
      <Dialog open={!!groupDialog} onOpenChange={() => setGroupDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <Users className="w-5 h-5 text-court-orange" />Squad Members
            </DialogTitle>
            <DialogDescription>
              {groupData[groupDialog]?.members?.length || 0} player(s) joined + host
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {/* Host */}
            {groupData[groupDialog] && (
              <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-sm">
                <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-heading text-xs font-bold">
                  {groupData[groupDialog].host_name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{groupData[groupDialog].host_name}</p>
                  <p className="text-[10px] text-orange-600 font-heading uppercase">Host</p>
                </div>
              </div>
            )}
            {/* Members */}
            {(groupData[groupDialog]?.members || []).map(m => (
              <div key={m.member_id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-sm">
                <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-heading text-xs font-bold">
                  {m.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{m.name}</p>
                  <p className="text-[10px] text-muted-foreground">{m.user_id ? "SetPlay Member" : "Guest"}{m.contact ? ` — ${m.contact}` : ""}</p>
                </div>
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                  disabled={removingMember === m.member_id}
                  onClick={() => handleRemoveMember(groupDialog, m.member_id)}
                  data-testid={`remove-member-${m.member_id}`}>
                  {removingMember === m.member_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                </Button>
              </div>
            ))}
            {(groupData[groupDialog]?.members || []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No one has joined yet. Share the link!</p>
            )}
          </div>
          <Button onClick={() => { setGroupDialog(null); const bid = groupDialog; setTimeout(() => handleShare(bid), 100); }}
            className="w-full mt-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-heading uppercase text-xs"
            data-testid="group-share-btn">
            <UserPlus className="w-4 h-4 mr-2" />Invite More Friends
          </Button>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <ShareDialog
        open={!!shareDialog}
        onOpenChange={() => setShareDialog(null)}
        shareUrl={shareDialog?.url}
        title={shareDialog?.title}
        description={shareDialog?.desc}
        type="social_booking"
      />
    </div>
  );
}
